import { useRef, useCallback } from "react";

type DebouncedFunction<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): void;
  flush: () => void;
};

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 500
): DebouncedFunction<T> {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastArgs = useRef<Parameters<T> | null>(null);

  const debouncedFn = useCallback((...args: Parameters<T>) => {
    lastArgs.current = args;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...(lastArgs.current!));
      timeoutRef.current = null;
      lastArgs.current = null;
    }, delay);
  }, [callback, delay]);

  return debouncedFn as DebouncedFunction<T>;
}
